/** @jsxImportSource @emotion/react */
import React from "react";
import { Menu } from "antd";
import { AuthUsecase } from "../../usecases/Auth/usecase";
import { useHistory } from "react-router-dom";
import { RouteInfo, RouteInfos } from "../../common/routeInfo";
import { UserGroupType } from "../../services/Auth/dto/signedIn.dto";
import { useAuthDispatch } from "../../usecases/Auth";
import * as Styles from "./styles";
import { useSettingState } from "../../usecases/Setting";

interface MenuItem {
  routeInfo: RouteInfo;
  children?: MenuItem[];
}

interface SideBarProps {
  role?: UserGroupType;
  basePath?: "admin" | "homebuilder" | "";
}

type SideBarPropsType = React.PropsWithChildren<SideBarProps>;

const SideBarComponent: React.FC<SideBarPropsType> = ({
  role,
  basePath,
}: SideBarPropsType) => {
  const [menuKey, setMenuKey] = React.useState("");
  const authDispatch = useAuthDispatch();
  const settingState = useSettingState();
  const history = useHistory();

  const onLogoutHandle = React.useCallback(async () => {
    const authUsecase = new AuthUsecase(authDispatch);
    await authUsecase.signOut();
    history.push("/login");
  }, [history, authDispatch]);

  const memoPath = React.useMemo(() => {
    return history.location.pathname;
  }, [history.location]);

  const onMenuClickHandle = React.useCallback(
    ({ key }) => {
      if (key === RouteInfos.Logout.key) {
        onLogoutHandle();
      } else {
        setMenuKey(key);
        let path = basePath ? `/${basePath}` : "";
        const keys = key.split("/");
        const routeInfo = RouteInfos[keys[0]];
        path += `${routeInfo.path.replace(":id", settingState.menuDetail?.id)}`;
        history.push(path);
      }
    },
    [onLogoutHandle, basePath, history, settingState.menuDetail?.id],
  );

  const menuItems = React.useMemo((): MenuItem[] => {
    switch (role) {
      case "SuperModerator":
        setMenuKey("");
        return [
          // {
          //   routeInfo: RouteInfos.Homes,
          // } as MenuItem,
          {
            routeInfo: RouteInfos.TenantUsers,
          } as MenuItem,
          {
            routeInfo: RouteInfos.Materials,
          } as MenuItem,
          {
            routeInfo: RouteInfos.Products,
          } as MenuItem,
          // {
          //   routeInfo: RouteInfos.Homebuyer,
          // } as MenuItem,
          // {
          //   routeInfo: RouteInfos.CompanyProfile,
          // } as MenuItem,
          // {
          //   routeInfo: RouteInfos.Profile,
          // } as MenuItem,
          {
            routeInfo: RouteInfos.Logout,
          } as MenuItem,
        ];
      case "Moderator":
        setMenuKey("");
        return [
          // {
          //   routeInfo: RouteInfos.Homes,
          // } as MenuItem,
          {
            routeInfo: RouteInfos.Materials,
          } as MenuItem,
          {
            routeInfo: RouteInfos.Products,
          } as MenuItem,
          // {
          //   routeInfo: RouteInfos.Homebuyer,
          // } as MenuItem,
          // {
          //   routeInfo: RouteInfos.Profile,
          // } as MenuItem,
          {
            routeInfo: RouteInfos.Logout,
          } as MenuItem,
        ];
      case "Sale":
        setMenuKey("");
        return [
          // {
          //   routeInfo: RouteInfos.Homes,
          // } as MenuItem,
          // {
          //   routeInfo: RouteInfos.Profile,
          // } as MenuItem,
          {
            routeInfo: RouteInfos.Materials,
          } as MenuItem,
          {
            routeInfo: RouteInfos.Products,
          } as MenuItem,
          {
            routeInfo: RouteInfos.Logout,
          } as MenuItem,
        ];
      default:
        return [];
    }
  }, [role]);

  React.useEffect(() => {
    menuItems.some((item) => {
      if (
        item.routeInfo.pathRegex &&
        memoPath.match(item.routeInfo.pathRegex)?.length
      ) {
        setMenuKey(item.routeInfo.key);
        return true;
      }

      if (item.children) {
        return item.children.some((childItem) => {
          if (
            childItem.routeInfo.pathRegex &&
            memoPath.match(childItem.routeInfo.pathRegex)?.length
          ) {
            setMenuKey(childItem.routeInfo.key);
          }

          if (childItem.children) {
            return childItem.children.some((leafItem) => {
              if (
                leafItem.routeInfo.pathRegex &&
                memoPath.match(leafItem.routeInfo.pathRegex)?.length
              ) {
                setMenuKey(leafItem.routeInfo.key);
                return true;
              }
              return false;
            });
          }
          return false;
        });
      }

      return false;
    });
  }, [memoPath, menuItems]);

  return (
    <Menu
      mode="inline"
      theme="dark"
      selectedKeys={[menuKey]}
      onClick={onMenuClickHandle}
    >
      {menuItems.map((item) => (
        <>
          <Menu.Item
            icon={
              <img
                css={Styles.menuIconStyle}
                src={
                  menuKey === item.routeInfo.key
                    ? item.routeInfo.activeIcon
                    : item.routeInfo.inactiveIcon
                }
              />
            }
            key={item.routeInfo.key}
          >
            {item.routeInfo.title}
          </Menu.Item>
          {item.children?.map((secondItem) => {
            const matchArray = memoPath.match(secondItem.routeInfo.pathRegex);
            if (matchArray === null || matchArray?.length === 0) {
              return <></>;
            }

            return (
              <>
                <Menu.Item
                  css={Styles.secondMenuStyle}
                  icon={
                    secondItem.routeInfo.activeIcon && (
                      <img
                        src={
                          menuKey === secondItem.routeInfo.key
                            ? secondItem.routeInfo.activeIcon
                            : secondItem.routeInfo.inactiveIcon
                        }
                      />
                    )
                  }
                  key={secondItem.routeInfo.key}
                >
                  {settingState.menuDetail
                    ? settingState.menuDetail?.title
                    : secondItem.routeInfo.title}
                </Menu.Item>
                {secondItem.children?.map((thirdItem) => {
                  const title = thirdItem.routeInfo.title;

                  return (
                    <>
                      <Menu.Item
                        css={Styles.thirdMenuStyle}
                        icon={
                          <img
                            src={
                              menuKey === thirdItem.routeInfo.key
                                ? thirdItem.routeInfo.activeIcon
                                : thirdItem.routeInfo.inactiveIcon
                            }
                          />
                        }
                        key={thirdItem.routeInfo.key}
                      >
                        {title}
                      </Menu.Item>
                    </>
                  );
                })}
              </>
            );
          })}
        </>
      ))}
    </Menu>
  );
};

export const SideBar = React.memo(SideBarComponent);
